import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
} from "@mui/material";
import { PlayArrow, CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { useRecordContext, useNotify } from "react-admin";
import { apiUrl } from "../../../httpClient";
import { handleApiError } from "../../../utils/errorHandler";
import { ADMIN_BASE } from "../../../config";
import type { ProviderTestResultDto } from "../../../types/api";

export const ProviderTestConnection = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<ProviderTestResultDto | null>(null);

  const handleTest = async () => {
    if (!record?.id) return;

    setTesting(true);
    setResult(null);
    try {
      const response = await fetch(
        apiUrl(`${ADMIN_BASE}/providers/${record.id}/test-connection`),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        if (data.success) {
          notify("Connection test successful", { type: "success" });
        } else {
          notify(data.message || "Connection test failed", { type: "error" });
        }
      } else {
        throw new Error("Failed to test connection");
      }
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
      setResult({
        success: false,
        message: appError.message,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setTesting(false);
    }
  };

  if (!record) return null;

  return (
    <Box>
      <Button
        startIcon={testing ? <CircularProgress size={16} /> : <PlayArrow />}
        onClick={handleTest}
        variant="contained"
        disabled={testing}
        sx={{ mb: 2 }}
      >
        {testing ? "Testing..." : "Test Connection"}
      </Button>

      {result && (
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              {result.success ? (
                <CheckCircle color="success" />
              ) : (
                <ErrorIcon color="error" />
              )}
              <Chip
                label={result.success ? "Success" : "Failed"}
                color={result.success ? "success" : "error"}
              />
              {result.responseTime && (
                <Typography variant="body2" color="text.secondary">
                  Response time: {result.responseTime}ms
                </Typography>
              )}
            </Box>

            <Typography variant="body1" gutterBottom>
              {result.message}
            </Typography>

            {result.error && (
              <Box
                component="pre"
                sx={{
                  p: 2,
                  bgcolor: "error.light",
                  borderRadius: 1,
                  overflow: "auto",
                  fontSize: "0.875rem",
                  mt: 2,
                }}
              >
                {result.error}
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
              Tested at: {new Date(result.timestamp).toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
